import { Fragment, useState } from 'react';
import type React from 'react';
import { Send, SlidersHorizontal } from 'lucide-react';
import { Button } from '../ui/Button';
import type { PropsCaptura } from './registro';
import { SobreSelector } from './SobreSelector';
import { nombresOrdenados } from '../../lib/sesion';
import type { CampoSchema } from '../../lib/sesion';

/**
 * Rótulos con acento para los campos numéricos más comunes de manifiestos
 * pre-v3 que aún no declaran `etiqueta` (`mapa_de_actores`,
 * `matriz_de_validacion`). Un campo fuera del mapa usa su nombre capitalizado.
 */
const ROTULOS: Record<string, string> = {
  interes: 'Interés',
  influencia: 'Influencia',
  impacto: 'Impacto',
  esfuerzo: 'Esfuerzo',
};

function rotuloCrudo(nombre: string): string {
  return ROTULOS[nombre] ?? nombre.charAt(0).toUpperCase() + nombre.slice(1);
}

/** Rango de enteros [min..max] para dibujar la fila de botones de escala. */
function valoresDe(campo: CampoSchema): number[] {
  const min = typeof campo.min === 'number' ? campo.min : 1;
  const max = typeof campo.max === 'number' ? campo.max : 5;
  const valores: number[] = [];
  for (let v = min; v <= max; v += 1) valores.push(v);
  return valores;
}

/** Un campo string está completo si, tras recortar, cumple el largo mínimo. */
function stringCompleto(campo: CampoSchema, valor: string): boolean {
  const texto = valor.trim();
  if (texto.length === 0) return false;
  if (typeof campo.min === 'number' && texto.length < campo.min) return false;
  return true;
}

/** ¿El campo number cubre exactamente la escala 1..5? (ausente = 1..5). */
function esEscala1a5(campo: CampoSchema): boolean {
  const min = typeof campo.min === 'number' ? campo.min : 1;
  const max = typeof campo.max === 'number' ? campo.max : 5;
  return min === 1 && max === 5;
}

const ESCALA_PLANO = [1, 2, 3, 4, 5];

/**
 * Captura de la primitiva `posicion` (Bolsillo, 380px, D5c declarativa).
 *
 * `captura.campos` (el `payload_schema.campos` del manifiesto) es la fuente de
 * verdad: cada campo `string` es un input de texto y cada campo `number` una
 * fila de botones de escala. El payload enviado usa los nombres EXACTOS de los
 * campos, que es lo que `validacion_aportes.py` (D6) exige en el backend.
 *
 * Caso especial PLANO TÁCTIL: cuando el schema declara EXACTAMENTE 2 campos
 * number con rango 1..5 (p. ej. interés/influencia o impacto/esfuerzo), en vez
 * de dos filas de escala se dibuja una cuadrícula 5×5: un toque fija ambos
 * valores, como poner una ficha sobre un plano. Cualquier otra combinación de
 * campos usa el comportamiento por filas de siempre.
 *
 * Retro-compat v1: sin campos declarados (o solo `valor`) se dibuja la escala
 * simple con las anclas de `captura.escala` y se envía `{valor}` al tocar.
 */
export function CapturaPosicion({ captura, deshabilitado, enviando, onEnviar }: PropsCaptura) {
  // Orden declarado por el manifiesto (ADR-0005 §1) y rótulo v3 con fallback.
  const nombres = nombresOrdenados(captura.campos);
  const rotuloDe = (nombre: string): string =>
    captura.campos[nombre]?.etiqueta ?? rotuloCrudo(nombre);
  const esSimple = nombres.length === 0 || (nombres.length === 1 && nombres[0] === 'valor');

  // Estado local por campo declarado (texto crudo y valor de escala elegido).
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [numeros, setNumeros] = useState<Record<string, number>>({});
  // Ancla de evaluación (ADR-0005 §3): elegir «sobre cuál» es parte del aporte.
  const [sobreSel, setSobreSel] = useState<string | null>(null);
  const requiereSobre = captura.sobre && captura.opciones.length > 0;

  /* ------------------- Rama v1: escala simple 1..5 -------------------- */
  if (esSimple) {
    const { min, max, minEtiqueta, maxEtiqueta } = captura.escala;
    const valores: number[] = [];
    for (let v = min; v <= max; v += 1) valores.push(v);

    return (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-5 gap-2">
          {valores.map((v) => (
            <button
              key={v}
              type="button"
              disabled={enviando || deshabilitado}
              onClick={() => onEnviar({ valor: v })}
              aria-label={`Posición ${v}`}
              className="flex min-h-touch items-center justify-center rounded-lg border border-border-strong bg-surface-raised font-display text-lg font-semibold text-content transition-colors duration-fast hover:border-primary hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50"
            >
              {v}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between font-sans text-xs text-content-muted">
          <span className="flex items-center gap-1">
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            {minEtiqueta}
          </span>
          <span>{maxEtiqueta}</span>
        </div>
      </div>
    );
  }

  /* ------------- Rama v2: multi-campo según payload_schema ------------ */
  const primerString = nombres.find((n) => captura.campos[n].tipo === 'string');
  const numericos = nombres.filter((n) => captura.campos[n].tipo === 'number');
  const esPlano = numericos.length === 2 && numericos.every((n) => esEscala1a5(captura.campos[n]));

  // Qué falta para poder enviar (alimenta el aviso bajo el botón, ADR-0005 §1).
  const faltantes = nombres.filter((nombre) => {
    const campo = captura.campos[nombre];
    if (campo.requerido === false) return false;
    if (campo.tipo === 'number') return typeof numeros[nombre] !== 'number';
    return !stringCompleto(campo, textos[nombre] ?? '');
  });
  const etiquetasFaltantes = [
    ...(requiereSobre && sobreSel === null ? ['elegir sobre cuál'] : []),
    ...faltantes.map(rotuloDe),
  ];
  const completo = etiquetasFaltantes.length === 0;
  const avisoFaltantes = !completo && (
    <p className="font-sans text-sm text-content-muted" aria-live="polite">
      Falta: {etiquetasFaltantes.join(', ')}
    </p>
  );
  const selectorSobre = requiereSobre && (
    <SobreSelector
      opciones={captura.opciones}
      valor={sobreSel}
      onElegir={setSobreSel}
      deshabilitado={enviando || deshabilitado}
    />
  );

  const enviar = () => {
    // Payload con los nombres EXACTOS del payload_schema; los campos opcionales
    // sin llenar se omiten para no chocar con el largo mínimo del backend.
    const payload: Record<string, unknown> = {};
    for (const nombre of nombres) {
      const campo = captura.campos[nombre];
      if (campo.tipo === 'number') {
        if (typeof numeros[nombre] === 'number') payload[nombre] = numeros[nombre];
      } else {
        const texto = (textos[nombre] ?? '').trim();
        if (texto.length > 0) payload[nombre] = texto;
      }
    }
    if (requiereSobre && sobreSel !== null) payload.sobre = sobreSel;
    onEnviar(payload);
  };

  // Input de texto de UN campo string (compartido entre plano y fallback),
  // con rótulo y ayuda del manifiesto v3 visibles (nunca una caja muda).
  const inputString = (nombre: string) => {
    const campo = captura.campos[nombre];
    return (
      <div key={nombre} className="flex flex-col gap-1.5">
        <span className="font-sans text-sm font-medium text-content">{rotuloDe(nombre)}</span>
        {campo.ayuda && (
          <span className="font-sans text-xs text-content-muted">{campo.ayuda}</span>
        )}
        <input
          type="text"
          value={textos[nombre] ?? ''}
          onChange={(e) => setTextos((prev) => ({ ...prev, [nombre]: e.target.value }))}
          placeholder={
            campo.placeholder ?? (nombre === primerString ? captura.placeholder : undefined)
          }
          aria-label={rotuloDe(nombre)}
          maxLength={typeof campo.max === 'number' ? campo.max : undefined}
          disabled={enviando || deshabilitado}
          className="min-h-touch w-full rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-base text-content placeholder:text-content-muted transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50"
        />
      </div>
    );
  };

  /* ----- Rama coordenadas: campos x/y en 0..100 (mapa de calor, O3) ----- */
  const esCoordenadas =
    numericos.length === 2 &&
    nombres.includes('x') &&
    nombres.includes('y') &&
    (captura.campos.x.max ?? 5) >= 10;
  if (esCoordenadas) {
    const vx = numeros.x;
    const vy = numeros.y;
    const haySeleccion = typeof vx === 'number' && typeof vy === 'number';

    const tocar = (e: React.MouseEvent<HTMLButtonElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
      const y = Math.min(100, Math.max(0, Math.round(((e.clientY - rect.top) / rect.height) * 100)));
      setNumeros((prev) => ({ ...prev, x, y }));
    };

    return (
      <div className="flex flex-col gap-4">
        {selectorSobre}
        {nombres.filter((n) => captura.campos[n].tipo !== 'number').map(inputString)}

        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={tocar}
            disabled={enviando || deshabilitado}
            aria-label="Tocá el punto exacto sobre la imagen"
            className="relative block aspect-[4/3] w-full overflow-hidden rounded-lg border border-border-strong bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50"
          >
            {captura.imagen && (
              <img
                src={captura.imagen}
                alt=""
                className="pointer-events-none absolute inset-0 h-full w-full object-cover"
              />
            )}
            {haySeleccion && (
              <span
                aria-hidden="true"
                className="absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-content-on-primary bg-primary shadow-raised"
                style={{ left: `${vx}%`, top: `${vy}%` }}
              />
            )}
          </button>
          <p className="font-sans text-sm text-content-muted" aria-live="polite">
            {haySeleccion
              ? 'Tu marca quedó puesta; podés tocar de nuevo para moverla.'
              : 'Tocá el lugar exacto que querés señalar.'}
          </p>
        </div>

        <Button
          variant="primary"
          fullWidth
          loading={enviando}
          disabled={!completo || deshabilitado}
          onClick={enviar}
          leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
        >
          Enviar mi aporte
        </Button>
        {avisoFaltantes}
      </div>
    );
  }

  /* --------- Rama plano táctil: 2 campos number con escala 1..5 -------- */
  if (esPlano) {
    const [ejeX, ejeY] = numericos;
    const vx = numeros[ejeX];
    const vy = numeros[ejeY];
    const haySeleccion = typeof vx === 'number' && typeof vy === 'number';

    return (
      <div className="flex flex-col gap-4">
        {selectorSobre}
        {nombres.filter((n) => captura.campos[n].tipo !== 'number').map(inputString)}

        <div className="flex flex-col gap-1.5">
          <span className="font-sans text-sm font-medium text-content">{rotuloDe(ejeY)}</span>
          <div
            className="grid items-center gap-1.5"
            role="grid"
            aria-label={`Plano de ${rotuloDe(ejeX)} y ${rotuloDe(ejeY)}`}
            style={{ gridTemplateColumns: 'auto repeat(5, minmax(0, 1fr))' }}
          >
            {[...ESCALA_PLANO].reverse().map((y) => (
              <Fragment key={y}>
                <span className="pr-1 text-right font-sans text-xs text-content-muted" aria-hidden="true">
                  {y}
                </span>
                {ESCALA_PLANO.map((x) => {
                  const elegida = vx === x && vy === y;
                  return (
                    <button
                      key={x}
                      type="button"
                      aria-pressed={elegida}
                      disabled={enviando || deshabilitado}
                      onClick={() => setNumeros((prev) => ({ ...prev, [ejeX]: x, [ejeY]: y }))}
                      aria-label={`${rotuloDe(ejeX)} ${x}, ${rotuloDe(ejeY)} ${y}`}
                      className={`flex aspect-square w-full items-center justify-center rounded-md border transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
                        elegida
                          ? 'border-primary bg-primary'
                          : 'border-border-strong bg-surface-raised hover:border-primary hover:bg-surface-sunken'
                      }`}
                    >
                      {elegida && (
                        <span className="h-2.5 w-2.5 rounded-full bg-content-on-primary" aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
              </Fragment>
            ))}
            <span aria-hidden="true" />
            {ESCALA_PLANO.map((x) => (
              <span key={x} className="text-center font-sans text-xs text-content-muted" aria-hidden="true">
                {x}
              </span>
            ))}
          </div>
          <span className="self-end font-sans text-sm font-medium text-content">{rotuloDe(ejeX)}</span>
          <p className="font-sans text-sm text-content-muted" aria-live="polite">
            {haySeleccion
              ? `${rotuloDe(ejeX)} ${vx} · ${rotuloDe(ejeY)} ${vy}`
              : 'Tocá una celda del plano para fijar tu posición.'}
          </p>
        </div>

        <Button
          variant="primary"
          fullWidth
          loading={enviando}
          disabled={!completo || deshabilitado}
          onClick={enviar}
          leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
        >
          Enviar mi aporte
        </Button>
        {avisoFaltantes}
      </div>
    );
  }

  /* ---------------- Fallback: filas de escala por campo ---------------- */
  return (
    <div className="flex flex-col gap-4">
      {selectorSobre}
      {nombres.map((nombre) => {
        const campo = captura.campos[nombre];

        if (campo.tipo === 'number') {
          const valores = valoresDe(campo);
          return (
            <div key={nombre} className="flex flex-col gap-2">
              <span className="font-sans text-sm font-medium text-content">{rotuloDe(nombre)}</span>
              {campo.ayuda && (
                <span className="font-sans text-xs text-content-muted">{campo.ayuda}</span>
              )}
              <div
                className="grid gap-2"
                role="radiogroup"
                aria-label={rotuloDe(nombre)}
                style={{ gridTemplateColumns: `repeat(${valores.length}, minmax(0, 1fr))` }}
              >
                {valores.map((v) => {
                  const elegido = numeros[nombre] === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={elegido}
                      disabled={enviando || deshabilitado}
                      onClick={() => setNumeros((prev) => ({ ...prev, [nombre]: v }))}
                      aria-label={`${rotuloDe(nombre)} ${v}`}
                      className={`flex min-h-touch items-center justify-center rounded-lg border font-display text-lg font-semibold text-content transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
                        elegido
                          ? 'border-primary bg-surface-sunken'
                          : 'border-border-strong bg-surface-raised hover:border-primary hover:bg-surface-sunken'
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }

        // Campo string: input de texto; el placeholder del manifiesto va en el
        // PRIMER campo string (es donde el facilitador escribió el ejemplo).
        return inputString(nombre);
      })}
      <Button
        variant="primary"
        fullWidth
        loading={enviando}
        disabled={!completo || deshabilitado}
        onClick={enviar}
        leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
      >
        Enviar mi aporte
      </Button>
      {avisoFaltantes}
    </div>
  );
}
